// The badge sweep: run the engine for every member. A dry run first (counts
// only, writes nothing), then a separate "Run for real" that asks before it
// writes. POST /api/badges/admin/sweep works whatever the engine switch says.
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useConfirm } from "../../../ui/components/ConfirmDialog";
import { BadgeArt } from "../art";
import { fmtInt } from "../collection/badgeFormat";
import { useRunAdminBadgeSweepMutation } from "./adminBadgesApi";
import {
  memberCodesText,
  sortSweepRows,
  sweepRows,
  sweepTotals,
  type SortDir,
  type SweepSortKey,
} from "./adminModel";
import type { AdminBadge, AdminSweepResult } from "./adminTypes";

interface Outcome {
  kind: "dry" | "real";
  backfill: boolean;
  at: Date;
  result: AdminSweepResult;
}

const COLUMNS: Array<{ key: SweepSortKey; label: string; numeric: boolean }> = [
  { key: "code", label: "Badge", numeric: false },
  { key: "copies", label: "Copies", numeric: true },
  { key: "fresh", label: "Fresh", numeric: true },
  { key: "history", label: "History", numeric: true },
];

/** A badge code that may wrap after its underscores (RACE_TIME_ / 250), never mid-word. */
function breakable(code: string): React.ReactNode {
  const parts = code.split("_");
  return parts.map((p, i) => (
    <React.Fragment key={i}>
      {p}
      {i < parts.length - 1 && (
        <>
          _<wbr />
        </>
      )}
    </React.Fragment>
  ));
}

function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function plural(n: number, one: string, many: string): string {
  return `${fmtInt(n)} ${n === 1 ? one : many}`;
}

function SweepResult({ outcome, defs }: { outcome: Outcome; defs: ReadonlyMap<string, AdminBadge> }) {
  const [sort, setSort] = useState<{ key: SweepSortKey; dir: SortDir }>({ key: "copies", dir: "desc" });
  const { result } = outcome;
  const totals = sweepTotals(result);
  const rows = useMemo(() => sortSweepRows(sweepRows(result), sort.key, sort.dir), [result, sort]);
  const members = Object.entries(result.byUser ?? {}).sort(([a], [b]) => a.localeCompare(b));
  const errors = result.providerErrors ?? [];
  const unowned = result.unownedCategories ?? [];

  const sortBy = (key: SweepSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "code" ? "asc" : "desc" }));

  const stats: Array<[string, number | null, string?]> = [
    ["Members checked", totals.checked],
    ["Getting badges", totals.receiving],
    [outcome.kind === "dry" ? "Copies to write" : "Copies written", totals.copies],
    ["Fresh", totals.fresh, "fresh"],
    ["History", totals.history, "history"],
  ];

  return (
    <div className="badm-result" data-testid="badm-sweep-result" data-kind={outcome.kind}>
      <p className="badm-result__head">
        <strong>{outcome.kind === "dry" ? "Dry run" : "Sweep written"}</strong> at {hhmm(outcome.at)}
        {outcome.backfill ? ", everything as history" : ""}
        {typeof result.durationMs === "number" ? `, took ${(result.durationMs / 1000).toFixed(1)} s` : ""}
        {outcome.kind === "dry" ? ". Nothing was written." : "."}
        {outcome.kind === "real" && typeof result.rankSnapshots === "number"
          ? ` Rank snapshots saved for ${plural(result.rankSnapshots, "member", "members")}.`
          : ""}
      </p>

      <dl className="badm-stats">
        {stats
          .filter(([, v]) => v !== null)
          .map(([label, v, tone]) => (
            <div key={label} className={`badm-stat${tone ? ` badm-stat--${tone}` : ""}`}>
              <dt>{label}</dt>
              <dd>{fmtInt(v as number)}</dd>
            </div>
          ))}
      </dl>

      {errors.length > 0 && (
        <div className="badm-note badm-note--bad" role="alert">
          <strong>Some stats could not be read.</strong> Badges from these providers were skipped:
          <ul>
            {errors.map((e) => (
              <li key={e.provider}>
                <code>{e.provider}</code>: {e.error}
              </li>
            ))}
          </ul>
        </div>
      )}
      {unowned.length > 0 && (
        <p className="badm-note">
          No provider owns these categories, so they were not checked: <code>{unowned.join(", ")}</code>
        </p>
      )}

      {rows.length === 0 ? (
        <p className="badm-muted">Nobody has anything new to earn.</p>
      ) : (
        <div className="badm-table-wrap">
          <table className="badm-table" aria-label={outcome.kind === "dry" ? "What the sweep would write" : "What the sweep wrote"}>
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={c.numeric ? "badm-num" : undefined}
                    aria-sort={sort.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <button type="button" className="badm-sort" onClick={() => sortBy(c.key)}>
                      {c.label}
                      <span className="badm-sort__arrow" aria-hidden="true">
                        {sort.key === c.key ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const def = defs.get(r.code);
                return (
                  <tr key={r.code} data-code={r.code}>
                    <td>
                      <span className="badm-table__badge">
                        {def ? (
                          <BadgeArt
                            category={def.category}
                            tier={def.tier}
                            number={def.number}
                            code={def.code}
                            name={def.name}
                            size={28}
                            variant="mini"
                          />
                        ) : (
                          <span className="badm-table__noart" aria-hidden="true" />
                        )}
                        <span className="badm-table__names">
                          <code className="badm-code">{breakable(r.code)}</code>
                          {def && <span className="badm-table__name">{def.name}</span>}
                        </span>
                      </span>
                    </td>
                    <td className="badm-num">{fmtInt(r.copies)}</td>
                    <td className="badm-num">{r.fresh ? fmtInt(r.fresh) : <span className="badm-zero">0</span>}</td>
                    <td className="badm-num">{r.history ? fmtInt(r.history) : <span className="badm-zero">0</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {members.length > 0 && (
        <details className="badm-members">
          <summary>Per member ({fmtInt(members.length)})</summary>
          <ul>
            {members.map(([username, codes]) => (
              <li key={username}>
                <strong>{username}</strong> <span className="badm-muted">({fmtInt(codes.length)})</span>{" "}
                <span className="badm-members__codes">{memberCodesText(codes)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

const SweepPanel: React.FC<{ defs: ReadonlyMap<string, AdminBadge> }> = ({ defs }) => {
  const [backfill, setBackfill] = useState(false);
  const [dry, setDry] = useState<Outcome | null>(null);
  const [shown, setShown] = useState<Outcome | null>(null);
  const [running, setRunning] = useState<"dry" | "real" | null>(null);
  const [runSweep] = useRunAdminBadgeSweepMutation();
  const confirm = useConfirm();

  const dryMatches = !!dry && dry.backfill === backfill;

  const runDry = async () => {
    setRunning("dry");
    try {
      const result = await runSweep({ dryRun: true, ...(backfill ? { backfill: true } : {}) }).unwrap();
      const outcome: Outcome = { kind: "dry", backfill, at: new Date(), result };
      setDry(outcome);
      setShown(outcome);
    } catch (error) {
      console.error("Badge sweep dry run failed:", error);
      toast.error("The dry run failed. See the console.");
    } finally {
      setRunning(null);
    }
  };

  const runReal = async () => {
    if (!dry || !dryMatches) return;
    const t = sweepTotals(dry.result);
    const ok = await confirm({
      title: "Run the badge sweep for real?",
      message: `It writes ${plural(t.copies, "copy", "copies")} for ${plural(t.receiving, "member", "members")}: ${fmtInt(
        t.fresh
      )} fresh, ${fmtInt(t.history)} history${backfill ? " (everything as history)" : ""}. Fresh copies show up as reveals, notify their members and can mint respect. The engine never removes a copy, so this cannot be undone from here.`,
      confirmLabel: "Run the sweep",
      tone: "danger",
    });
    if (!ok) return;
    setRunning("real");
    try {
      const result = await runSweep({ dryRun: false, ...(backfill ? { backfill: true } : {}) }).unwrap();
      setShown({ kind: "real", backfill, at: new Date(), result });
      setDry(null);
      toast.success("Badge sweep done.");
    } catch (error) {
      console.error("Badge sweep failed:", error);
      toast.error("The badge sweep failed. See the console.");
    } finally {
      setRunning(null);
    }
  };

  const dryTotals = dry ? sweepTotals(dry.result) : null;

  return (
    <section className="badm-panel" aria-labelledby="badm-sweep-title">
      <header className="badm-panel__head" style={{ background: "#ffe95d" }}>
        <h2 id="badm-sweep-title" className="badm-panel__title">
          Sweep
        </h2>
        <span className="badm-panel__aside">every member, every badge</span>
      </header>
      <div className="badm-panel__body">
        <p className="badm-lead">
          Checks every member against every active badge and writes the copies they qualify for. Start with a dry run:
          it counts what the sweep would write and writes nothing.
        </p>

        <label className="badm-check">
          <input type="checkbox" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} />
          <span>
            <strong>Mark everything as history</strong>
            <span className="badm-check__help">
              Every copy this sweep writes lands as history, whatever its date: collected quietly, no reveal, no
              notification, no respect. Use it for the first sweep after the deploy, so this week's streaks and races
              do not pop up as new badges on top of the whole backlog. Without it, only copies earned more than 72
              hours ago count as history.
            </span>
          </span>
        </label>

        <div className="badm-actions">
          <button type="button" className="badm-btn badm-btn--primary" onClick={runDry} disabled={running !== null}>
            {running === "dry" ? "Counting" : "Dry run"}
          </button>
        </div>

        {shown && <SweepResult key={`${shown.kind}-${shown.at.getTime()}`} outcome={shown} defs={defs} />}

        <div className="badm-danger" role="group" aria-labelledby="badm-real-title">
          <h3 id="badm-real-title" className="badm-danger__title">
            Run for real
          </h3>
          {dry && dryMatches && dryTotals ? (
            <p className="badm-danger__text">
              The dry run at {hhmm(dry.at)} found {plural(dryTotals.copies, "copy", "copies")} for{" "}
              {plural(dryTotals.receiving, "member", "members")} ({fmtInt(dryTotals.fresh)} fresh,{" "}
              {fmtInt(dryTotals.history)} history). Running it writes them, counts holders for every badge and saves
              today's rank snapshot.
            </p>
          ) : dry ? (
            <p className="badm-danger__text">
              You changed "Mark everything as history" since the dry run. Do a new dry run with this setting first.
            </p>
          ) : (
            <p className="badm-danger__text">Do a dry run first. The real run uses the same setting.</p>
          )}
          <button
            type="button"
            className="badm-btn badm-btn--danger"
            onClick={runReal}
            disabled={!dryMatches || running !== null}
          >
            {running === "real" ? "Writing" : "Run for real"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default SweepPanel;
